/**
 * ItineraryContent — web port of `apps/WunderGuide/components/itinerary/ItineraryOverlay.tsx`.
 *
 * Uses the SAME `@wg/api/itinerary` client + `ItineraryGroupedResponse` type as
 * the RN client. RN's flow:
 *   1. listItineraries({status: 'planning'}) → first item id
 *   2. getItineraryGrouped(tripId) → {days[], unscheduled[]}
 *   3. Render day-by-day timeline with activity cards.
 *
 * If no trip exists, render the empty-state with mock cards (matches RN
 * SCREENSHOT_MODE).
 */
import React, { useEffect, useMemo, useState } from 'react';
import itineraryApi from '../../api/itineraryApi';
import type { ItineraryGroupedResponse, ItineraryItem } from '@wg/types';
import Spinner from '../../common/Spinner';
import { useDrawer } from '../../context/DrawerContext';
import './ItineraryContent.css';

type Mode = 'plan' | 'day';

const ItineraryContent: React.FC = () => {
  const [mode, setMode] = useState<Mode>('plan');
  const [tripId, setTripId] = useState<string | null>(null);
  const [grouped, setGrouped] = useState<ItineraryGroupedResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { openTripWizard } = useDrawer();

  // Find the latest planning itinerary.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const result = await itineraryApi.listItineraries({
          status: 'planning',
        });
        if (cancelled) return;
        if (result.itineraries.length > 0) {
          setTripId(result.itineraries[0].id);
        } else {
          setTripId(null);
          setLoading(false);
        }
      } catch (err: any) {
        if (cancelled) return;
        if (err?.response?.status === 404) {
          setTripId(null);
        } else {
          setError('Failed to load itinerary.');
        }
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch grouped days for the resolved trip.
  useEffect(() => {
    if (!tripId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const data = await itineraryApi.getItineraryGrouped(tripId);
        if (!cancelled) setGrouped(data);
      } catch (err: any) {
        if (!cancelled) setError('Failed to load itinerary.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tripId]);

  const flatItems = useMemo(
    () =>
      (grouped?.days ?? []).flatMap(d =>
        d.items.map(i => ({
          ...i,
          _date: d.date_display,
          _dow: d.day_of_week,
        })),
      ),
    [grouped],
  );

  return (
    <div className="wg-itin">
      {/* Mode tabs + sparkle */}
      <div className="wg-itin__topbar">
        <div className="wg-itin__tabs">
          <button
            type="button"
            className={`wg-itin__tab${mode === 'plan' ? ' is-active' : ''}`}
            onClick={() => setMode('plan')}
          >
            <ListIcon />
            <span>Plan</span>
          </button>
          <button
            type="button"
            className={`wg-itin__tab${mode === 'day' ? ' is-active' : ''}`}
            onClick={() => setMode('day')}
          >
            <CalendarIcon />
            <span>Day</span>
          </button>
        </div>

        <button
          type="button"
          className="wg-itin__sparkle"
          aria-label="Generate"
          onClick={openTripWizard}
        >
          <SparkleIcon />
        </button>
      </div>

      {loading && (
        <div className="wg-itin__center">
          <Spinner size="large" />
        </div>
      )}

      {!loading && error && <div className="wg-itin__center">{error}</div>}

      {!loading && !error && !tripId && (
        <div className="wg-itin__empty">
          <div className="wg-itin__empty-icon">
            <CalendarIcon />
          </div>
          <h3>No trip yet</h3>
          <p>
            Tell WunderGuide where you’re going and we’ll plan a day-by-day
            itinerary you can edit.
          </p>
          <button
            type="button"
            className="wg-itin__cta"
            onClick={openTripWizard}
          >
            Plan a new trip
          </button>
        </div>
      )}

      {!loading && !error && tripId && grouped && grouped.days.length === 0 && (
        <div className="wg-itin__empty">
          <h3>Your trip is empty</h3>
          <p>Add activities, deals, or bookings to get started.</p>
        </div>
      )}

      {!loading && !error && grouped && grouped.days.length > 0 && (
        <div className="wg-itin__timeline">
          {mode === 'plan'
            ? grouped.days.map(day => (
                <section className="wg-itin__day" key={day.date}>
                  <div className="wg-itin__date">
                    {day.day_of_week}, {day.date_display}
                  </div>
                  {day.items.map((item, i) => (
                    <ItineraryRow
                      key={item.id}
                      item={item}
                      gap={gapBetween(day.items[i], day.items[i + 1])}
                    />
                  ))}
                </section>
              ))
            : flatItems.map(item => (
                <ItineraryRow key={item.id} item={item as ItineraryItem} />
              ))}
        </div>
      )}

      <button type="button" className="wg-itin__fab" aria-label="Add item">
        <PlusIcon />
      </button>
    </div>
  );
};

export default ItineraryContent;

/* ── ItineraryRow ─────────────────────────────────────────────────────── */

const ItineraryRow: React.FC<{ item: ItineraryItem; gap?: string | null }> = ({
  item,
  gap,
}) => {
  const kind: 'bookmark' | 'deal' | 'activity' =
    item.type === 'recommendation'
      ? 'deal'
      : item.type === 'booking' ||
          item.type === 'flight' ||
          item.type === 'hotel'
        ? 'bookmark'
        : 'activity';

  const Icon =
    kind === 'deal' ? DealIcon : kind === 'bookmark' ? BookmarkIcon : DotIcon;

  return (
    <>
      <article className="wg-itin__card">
        <span className={`wg-itin__icon wg-itin__icon--${kind}`}>
          <Icon />
        </span>
        <div className="wg-itin__text">
          <div className="wg-itin__title">{item.title}</div>
          {item.location?.name && (
            <div className="wg-itin__loc">
              <PinIcon />
              <span>{item.location.name}</span>
            </div>
          )}
        </div>
        {item.scheduled_time && (
          <div className="wg-itin__time">{item.scheduled_time}</div>
        )}
        <span className="wg-itin__chev">
          <ChevronIcon />
        </span>
      </article>

      {gap && (
        <div className="wg-itin__gap" aria-hidden>
          <span className="wg-itin__gap-dot">
            <PlusIcon />
          </span>
          <span className="wg-itin__gap-line" />
          <span className="wg-itin__gap-label">{gap}</span>
          <span className="wg-itin__gap-line" />
        </div>
      )}
    </>
  );
};

/** Compute "1h 30m" between two scheduled_time HH:MM strings on the same day. */
function gapBetween(a?: ItineraryItem, b?: ItineraryItem): string | null {
  if (!a || !b || !a.scheduled_time || !b.scheduled_time) return null;
  const [ah, am] = a.scheduled_time.split(':').map(Number);
  const [bh, bm] = b.scheduled_time.split(':').map(Number);
  let mins = bh * 60 + bm - (ah * 60 + am);
  if (a.duration_minutes) mins -= a.duration_minutes;
  if (mins <= 0) return null;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

/* ── Glyphs ──────────────────────────────────────────────────────────── */

function ListIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="8" y1="6" x2="20" y2="6" />
      <line x1="8" y1="12" x2="20" y2="12" />
      <line x1="8" y1="18" x2="20" y2="18" />
      <circle cx="4" cy="6" r="1.4" fill="currentColor" />
      <circle cx="4" cy="12" r="1.4" fill="currentColor" />
      <circle cx="4" cy="18" r="1.4" fill="currentColor" />
    </svg>
  );
}
function CalendarIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="4" width="18" height="17" rx="2.5" />
      <line x1="3" y1="9.5" x2="21" y2="9.5" />
      <line x1="8" y1="2.5" x2="8" y2="6" />
      <line x1="16" y1="2.5" x2="16" y2="6" />
    </svg>
  );
}
function SparkleIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <path d="M12 3l1.6 5.4L19 10l-5.4 1.6L12 17l-1.6-5.4L5 10l5.4-1.6z" />
    </svg>
  );
}
function BookmarkIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M19 21l-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
    </svg>
  );
}
function DealIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M20.6 12.6 12 21.2c-.78.78-2.05.78-2.83 0L2.5 14.5c-.32-.32-.5-.75-.5-1.2V4a2 2 0 0 1 2-2h9.3c.45 0 .88.18 1.2.5l6.1 6.1c.78.78.78 2.05 0 2.83Z" />
      <circle cx="7.5" cy="7.5" r="1.5" />
    </svg>
  );
}
function DotIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <circle cx="12" cy="12" r="4" />
    </svg>
  );
}
function PinIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 22s7-7.58 7-13a7 7 0 1 0-14 0c0 5.42 7 13 7 13z" />
      <circle cx="12" cy="9" r="2.6" />
    </svg>
  );
}
function ChevronIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}
function PlusIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}
